import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center  gap-2 cursor-pointer whitespace-nowrap rounded-md text-sm md:text-md font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-brand-gradient shadow-[inset_0px_4px_6px_#ffffff66] text-primary-foreground hover:opacity-90",
        flat:
          "bg-primary text-primary-foreground hover:bg-primary/90",
        // Fydo 2.0 CTAs - pair with size="pill" / "pillSm" for the pill
        // shape (the other sizes set their own radius and win the merge).
        // The solid ones carry a transparent 2px border so they line up
        // with brandOutline when placed side by side.
        brand:
          "border-2 border-transparent bg-primary text-white hover:bg-primary/90",
        brandOutline:
          "border-2 border-ink bg-transparent text-ink hover:bg-ink hover:text-white",
        white: "border-2 border-transparent bg-white text-ink hover:bg-paper",
        // Outline button on dark/red backgrounds (2.0 hero secondary CTA).
        whiteOutline:
          "border-2 border-white/80 bg-transparent text-white hover:bg-white/10",
        "gold-grad":
          "btn-grad-gold text-[#282828]  px-4 py-3 rounded-[8px] font-bold",
        outline:
          "bg-white/10 text-secondary-foreground hover:opacity-80 border",
        outlineb:
          "border-zinc-50/10 border-2 bg-white/10 text-secondary-foreground hover:opacity-90",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",

      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        pill: "h-auto rounded-full px-6 py-[13px] text-base leading-none",
        pillSm: "h-auto rounded-full px-5 py-[11px] text-[15px] leading-none",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
